import { useEffect, useMemo, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import '../../accounting/accounting.css';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame
import './divisionDetail.css';
import './viewLock.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate, showDialog } from '../../../components/confirm';
import { runWithAuditReason } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import {
  useCompanies, useCompany, useSaveCompany, useDeleteCompany,
  useStates, useStateCountries, useTimeZones, useTestSmtp, errMessage, type Company, type CompanyDetail,
} from './systemMasters';
import { useCities } from './city';
import { LookupSelect } from './LookupSelect';
import { DivisionDepartmentsPanel } from './DivisionDepartmentsPanel';
import { DivisionPositionsPanel } from './DivisionPositionsPanel';
import { DivisionCertificatePanel } from './DivisionCertificatePanel';
import { EMPTY_DIVISION_STAGING, commitDivisionStaging, hasDivisionStaging, type DivisionStaging } from './systemMasters';
import { useAuthStore } from '../../../auth/store';

interface Props { onHome?: () => void; }

type Tab = 'basic' | 'contact' | 'logo' | 'qrcode' | 'mtf' | 'smtp';
const LANGUAGES = [{ v: 0, t: 'Chinese' }, { v: 1, t: 'English' }];
const BLANK: CompanyDetail = {
  id: '', companyName: '', webSite: '', address: '', language: 1, country: null, state: null, city: null,
  isValid: true, remark: '', contactName: '', officePhone: '', mobilePhone: '', fax: '', homePhone: '', otherPhone: '', email: '',
  zip: '', timeZone: null, logo: null, qrCode: null, measureCvFlag: null,
  enableSmtp: false, mailServer: '', portNo: null, ssl: false, fromMail: '', fromMailPassword: '', displayName: '',
};

/**
 * Settings ▸ System Management ▸ Division (the legacy Company master, shown as
 * "Division" in XAF). Phase 1 = Basic Information + Contact tabs (images, SMTP
 * config and the Departments/Positions M2M tabs are deferred). Country → State →
 * City cascade client-side; required: Division, Address, Office Phone, Country,
 * State, City. Update/Delete audit as FormName "Division"; delete is blocked when
 * employees or departments still reference it (LIMS OnDeleting parity).
 */
export function CompanyPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useCompanies();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveCompany();
  const del = useDeleteCompany();

  const { data: countries = [] } = useStateCountries();
  const { data: states = [] } = useStates();
  const { data: cities = [] } = useCities();
  const { data: timezones = [] } = useTimeZones();

  const [search, setSearch] = useState('');

  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  const [editId, setEditId] = useState<string | null | undefined>(undefined);   // undefined = closed, null = New, guid = Edit
  const { data: detail } = useCompany(editId ?? undefined);
  const [form, setForm] = useState<CompanyDetail>(BLANK);
  const [tab, setTab] = useState<Tab>('basic');
  const [btab, setBtab] = useState<'departments' | 'positions' | 'certificate'>('departments');   // bottom nested-grid tabs
  const [imgEditing, setImgEditing] = useState<Record<string, boolean>>({});   // per-image: show Browse after clicking the edit pencil
  const [err, setErr] = useState('');
  // Required-field messages, one per field, shown under that field (owner request 2026-10-14).
  const [fieldErrs, setFieldErrs] = useState<Partial<Record<'companyName' | 'officePhone' | 'address' | 'city' | 'state' | 'country' | 'timeZone' | 'utc', string>>>({});
  const clearFieldErr = (k: string) => setFieldErrs((fe) => (k in fe ? Object.fromEntries(Object.entries(fe).filter(([x]) => x !== k)) : fe));
  /** The message under a field, lined up with its box. */
  const fieldErr = (k: keyof typeof fieldErrs) => (fieldErrs[k] ? <div className="div-field-err">{fieldErrs[k]}</div> : null);

  useEffect(() => { if (editId === null) setForm({ ...BLANK }); }, [editId]);
  useEffect(() => { if (detail) setForm(detail); }, [detail]);


  // View → Edit (owner request 2026-10-12): a click on a row opens the record READ-ONLY with an Edit
  // button; Edit turns the boxes and the Save buttons on. New always opens editable.
  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const viewOnly = mode === 'view' && !!editId;
  // New division: what its Departments / Positions / Certificate tabs hold until the first Save (G5 parity,
  // 2026-10-14) — applied by doSave right after the division is created.
  const [staging, setStaging] = useState<DivisionStaging>(EMPTY_DIVISION_STAGING);
  const qc = useQueryClient();
  const openNew = () => { setStaging(EMPTY_DIVISION_STAGING); setMode('edit'); setEditId(null); setForm({ ...BLANK }); setTab('basic'); setErr(''); setFieldErrs({}); };
  const openEdit = (id: string) => { setMode('edit'); setEditId(id); setTab('basic'); setErr(''); setFieldErrs({}); };
  const openView = (id: string) => { setMode('view'); setEditId(id); setTab('basic'); setErr(''); setFieldErrs({}); };
  const close = () => { setEditId(undefined); setStaging(EMPTY_DIVISION_STAGING); };

  // ── SMTP Configuration ▸ Test Connection — legacy TestMailConnectionAction_Execute
  //    (DivisionDepartmentController): check the fields, send one fixed test mail with the values
  //    ON SCREEN, then "SMTP connection successful." in the banner. No popup (owner request
  //    2026-10-09). The mail goes to the signed-in user's own address — legacy's first choice, the
  //    lab's default recipient list, is deliberately not used (2026-09-27: test mails must not
  //    reach real customers). The API still allows administrators / the service account only. ──
  const ownEmail = useAuthStore((st) => st.user?.email ?? '');
  const testSmtp = useTestSmtp(editId ?? '');
  const testConnection = async () => {
    if (!form.enableSmtp) return;
    // Legacy checks, legacy wording. A saved division's password is held by the server, so it is
    // only asked for here on a division that has not been saved yet.
    if (!form.fromMail?.trim() || (!editId && !form.fromMailPassword)) { notify.error('Please enter From Mail ID and password.'); return; }
    if (!form.mailServer?.trim()) { notify.error('Please enter Mail Server.'); return; }
    if (!form.portNo || form.portNo <= 0) { notify.error('Please enter a valid Port number.'); return; }
    if (!/^[^s@,;<>]+@[^s@,;<>]+.[^s@,;<>]+$/.test(ownEmail.trim())) {
      notify.error('Your user has no valid email address to receive the test mail.');
      return;
    }
    try {
      const r = await testSmtp.mutateAsync({
        mailServer: form.mailServer, portNo: form.portNo, ssl: form.ssl,
        fromMail: form.fromMail, fromMailPassword: form.fromMailPassword, displayName: form.displayName,
        toEmail: ownEmail.trim(), message: 'This is a test mail sent to test the SMTP connection.',
      });
      if (r.ok) notify.success('SMTP connection successful.');
      else await showDialog({ type: 'error', title: 'Test Connection', message: r.message, note: r.detail });
    } catch (e) { notify.error(errMessage(e)); }
  };
  // Cancel / breadcrumb / record arrows: ask before dropping unsaved edits (standing rule 15).
  const leave = () => { void guardedExit(close); };

  const set = (patch: Partial<CompanyDetail>) => setForm((f) => ({ ...f, ...patch }));
  // No cascade — Country/State/City are independent full-list lookups (see opts below).
  const onCountry = (v: string | null) => set({ country: v });
  const onState = (v: string | null) => set({ state: v });

  // FULL lists, no cascade (owner decision 2026-06-25) — the legacy City→State→Country FKs are
  // inconsistent in this data, so strict cascading would hide valid options. Matches the Employee form.
  const countryOpts = useMemo(() => countries.map((c) => ({ id: c.id, label: c.name })), [countries]);
  const stateOpts = useMemo(() => states.map((s) => ({ id: s.id, label: s.longName ?? s.shortName ?? '' })), [states]);
  const cityOpts = useMemo(() => cities.map((c) => ({ id: c.id, label: c.cityName ?? '' })), [cities]);
  const dlgUtc = useMemo(() => timezones.find((t) => t.id === form.timeZone)?.utc ?? '', [timezones, form.timeZone]);

  const doSave = async (after: 'stay' | 'close' | 'new') => {
    // Check every required field at once; each message shows under its own field.
    const fe: typeof fieldErrs = {};
    if (!form.companyName?.trim()) fe.companyName = 'Division must not be empty.';
    if (!form.officePhone?.trim()) fe.officePhone = 'Office Phone must not be empty.';
    if (!form.address?.trim()) fe.address = 'Address must not be empty.';
    if (!form.city) fe.city = 'City must not be empty.';
    if (!form.state) fe.state = 'State must not be empty.';
    if (!form.country) fe.country = 'Country must not be empty.';
    if (!form.timeZone) { fe.timeZone = 'Time Zone must not be empty.'; fe.utc = 'UTC must not be empty.'; }
    setFieldErrs(fe);
    if (Object.keys(fe).length > 0) { setTab('basic'); setErr(''); return; }
    setErr('');
    const wasNew = !form.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (!wasNew) {
      const r = await gateReason('Division', 'edit');
      if (r === null) return;
      reason = r;
    }
    let newId: string | null = form.id || null;
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        newId = await save.mutateAsync(form);
        // A new division: apply what its tabs held, now that the id exists.
        if (wasNew && newId && hasDivisionStaging(staging)) {
          await commitDivisionStaging(newId, staging);
          setStaging(EMPTY_DIVISION_STAGING);
          void qc.invalidateQueries({ queryKey: ['sm-division-departments'] });
          void qc.invalidateQueries({ queryKey: ['sm-division-positions'] });
          void qc.invalidateQueries({ queryKey: ['sm-division-certificates'] });
          void qc.invalidateQueries({ queryKey: ['sm-certificates-linkable'] });
        }
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        if (after === 'new') { openNew(); return; }
        if (wasNew && newId) setEditId(newId);   // new → switch to edit mode on the fresh row
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one division to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} division${selectedIds.size === 1 ? '' : 's'}?` })) return;
    const reason = await gateReason('Division', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  // Detail shortcuts through the shared hotkey layer (standing rule 14).
  useHotkeys(editId === undefined ? [] : viewOnly ? [
    { combo: 'alt+e', label: 'Edit', group: 'Division', order: 1, run: () => setMode('edit') },
    { combo: 'f2', label: 'Edit', group: 'Division', order: 2, run: () => setMode('edit') },
    { combo: 'esc', label: 'Close', group: 'Division', order: 3, allowInInput: false, run: close },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Division', order: 1, run: () => void doSave('stay') },
    { combo: 'mod+shift+s', label: 'Save & New', group: 'Division', order: 2, run: () => void doSave('new') },
    { combo: 'mod+enter', label: 'Save & Close', group: 'Division', order: 3, run: () => void doSave('close') },
    { combo: 'esc', label: 'Cancel', group: 'Division', order: 4, allowInInput: false, run: leave },
  ]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.name, r.officePhone, r.address, r.city, r.state, r.country].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const cmp = (get: (r: Company) => string | null) => (a: Company, b: Company) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });

  // Inline (in-grid) cascading option lists for the edit row.
  // Inline edit reuses the same FULL (non-cascading) State/City lists as the dialog.
  const tzOpts = useMemo(() => timezones.filter((t) => t.active).map((t) => ({ id: t.id, label: t.timeZone ?? '' })), [timezones]);

  // Columns mirror the legacy Company_ListView (Name, OfficePhone, Address, City, State, Zip,
  // Country, Department, TimeZone, UTC). The row icon opens the Detail View in edit mode (owner request
  // 2026-10-14; the full-row in-place editor it used to start is removed).
  const columns: AppColumn<Company>[] = [
    {
      field: 'detailedit', header: '', noExport: true, sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => (
        <button className="pts-icon-btn edit" title="Edit" aria-label={`Edit ${r.name ?? 'division'}`}
          onClick={(e) => { e.stopPropagation(); openEdit(r.id); }}><i className="ti ti-edit" /></button>
      ),
    },
    {
      // LIMS Name column is narrow — long names WRAP to two lines (matched here via width + normal wrap).
      field: 'name', header: 'Name', width: 190, text: (r) => r.name ?? '', compare: cmp((r) => r.name),
      render: (r) => (<span style={{ whiteSpace: 'normal', display: 'block' }}>{r.name ?? ''}</span>),
    },
    { field: 'officePhone', header: 'OfficePhone', minWidth: 140, sortable: false, text: (r) => r.officePhone ?? '' },
    { field: 'address', header: 'Address', minWidth: 170, sortable: false, text: (r) => r.address ?? '' },
    { field: 'city', header: 'City', minWidth: 120, sortable: false, text: (r) => r.city ?? '' },
    { field: 'state', header: 'State', minWidth: 110, sortable: false, text: (r) => r.state ?? '' },
    { field: 'zip', header: 'Zip', minWidth: 80, sortable: false, text: (r) => r.zip ?? '' },
    { field: 'country', header: 'Country', minWidth: 140, sortable: false, text: (r) => r.country ?? '' },
    { field: 'department', header: 'Department', minWidth: 130, sortable: false, text: (r) => r.department ?? '' },
    { field: 'timeZone', header: 'TimeZone', minWidth: 150, sortable: false, text: (r) => r.timeZone ?? '' },
    { field: 'utc', header: 'UTC', minWidth: 90, sortable: false, text: (r) => r.utc ?? '' },
  ];
  // Toolbar Edit: the ONE ticked row opens in the Detail View, editable.
  const editSelected = () => { if (selectedIds.size === 1) openEdit(String([...selectedIds][0])); };

  // ── Dialog field helpers ────────────────────────────────────────────────────
  const text = (label: string, key: keyof CompanyDetail, required = false) => (
    <>
    <div className="acc-field-h">
      <label className={`acc-label${required ? ' acc-label-req' : ''}`}>{label}{required && <span className="req">*</span>}</label>
      <div className="acc-fh-control">
        <input className="acc-input" value={(form[key] as string) ?? ''} onChange={(e) => { set({ [key]: e.target.value } as Partial<CompanyDetail>); if (err) setErr(''); clearFieldErr(key); }} />
      </div>
    </div>
    {fieldErr(key as keyof typeof fieldErrs)}
    </>
  );
  const lookup = (label: string, key: 'country' | 'state' | 'city', opts: { id: string; label: string }[], onChange: (v: string | null) => void, required = false) => (
    <>
    <div className="acc-field-h">
      <label className={`acc-label${required ? ' acc-label-req' : ''}`}>{label}{required && <span className="req">*</span>}</label>
      <div className="acc-fh-control acc-lookup">
        <LookupSelect value={(form[key] as string) ?? null} options={opts} onChange={(v) => { onChange(v); if (err) setErr(''); if (v) clearFieldErr(key); }} />
        {form[key] && <button className="acc-lookup-btn" title="Erase" onClick={() => onChange(null)}><i className="ti ti-eraser" /></button>}
      </div>
    </div>
    {fieldErr(key)}
    </>
  );

  // Image tab (Logo / QR Code / Mandatory Testing Flag) — LIMS shows the image with edit/erase icons;
  // the Browse button appears only AFTER clicking the edit pencil (empty → Browse straight away).
  // Stored as varbinary (byte[]); base64 round-trips via JSON, like the Employee Photo/Signature.
  const onPickImage = (key: 'logo' | 'qrCode' | 'measureCvFlag', file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const r = String(reader.result); set({ [key]: r.includes(',') ? r.split(',')[1] : r } as Partial<CompanyDetail>); };
    reader.readAsDataURL(file);
    setImgEditing((s) => ({ ...s, [key]: false }));
  };
  const imageField = (label: string, key: 'logo' | 'qrCode' | 'measureCvFlag') => {
    const b64 = form[key];
    const editing = imgEditing[key] ?? false;
    const browse = (text = 'Browse…') => (
      <label className="ca-btn acc-img-browse">{text}
        <input type="file" accept="image/*" hidden onChange={(e) => onPickImage(key, e.target.files?.[0])} />
      </label>
    );
    return (
      <div className="acc-tab-pane">
        <div className="acc-img-field">
          <label className="acc-label">{label}</label>
          {b64 ? (
            <>
              <div className="acc-img-box"><img src={`data:image/png;base64,${b64}`} alt={label} /></div>
              <div className="acc-img-acts">
                <button type="button" className="pts-icon-btn edit" title="Edit" onClick={() => setImgEditing((s) => ({ ...s, [key]: true }))}><i className="ti ti-edit" /></button>
                <button type="button" className="pts-icon-btn" title="Erase" onClick={() => { set({ [key]: null } as Partial<CompanyDetail>); setImgEditing((s) => ({ ...s, [key]: false })); }}><i className="ti ti-eraser" /></button>
              </div>
              {editing && <div className="acc-img-acts">{browse()}</div>}
            </>
          ) : (
            <>
              <div className="acc-img-box acc-img-box-empty"><i className="ti ti-photo acc-img-ph" /></div>
              <div className="acc-img-acts">{browse()}</div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Full-page detail (New / Edit) — replaces the list while open (standing rule 12; owner
  //    request 2026-10-09: "need to open the detail view not the popup"). Same frame as the City
  //    form: title + breadcrumb + record arrows, action bar top and bottom, then the tabs. ──
  if (editId !== undefined) {
    const recordName = editId ? (form.companyName ?? '').trim() || 'Division' : 'New Division';
    const curIdx = editId ? rows.findIndex((r) => r.id === editId) : -1;
    const prevRow = curIdx > 0 ? rows[curIdx - 1] : null;
    const nextRow = curIdx >= 0 && curIdx < rows.length - 1 ? rows[curIdx + 1] : null;
    const actions = (bottom?: boolean) => viewOnly ? (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="sd-abtn cancel" onClick={close} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
      </div>
    ) : (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => void doSave('stay')} disabled={save.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> Save</button>
        <button className="sd-abtn" onClick={() => void doSave('close')} disabled={save.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
        <button className="sd-abtn" onClick={() => void doSave('new')} disabled={save.isPending} title={`Save and New (${formatCombo('mod+shift+s')})`}><span className="tb-i">📄</span> Save and New</button>
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" onClick={leave} disabled={save.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
      </div>
    );
    return (
      <div className="bl-page mlv-host">
        {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
            there are unsaved changes. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
        </div>
        <div className="bl-grid-area">
          <div className="sd-wrap">
            <div className="sd-head">
              <div>
                <h2>Division – {recordName}</h2>
                <div className="sd-bc">
                  <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                  <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                  <a onClick={leave}>Division</a><i className="ti ti-chevron-right" /><span>{recordName}</span>
                </div>
              </div>
              <div className="sd-navarrows">
                <button className="sd-na" title="Previous record" disabled={!prevRow || save.isPending} onClick={() => prevRow && void guardedExit(() => (viewOnly ? openView : openEdit)(prevRow.id))}>‹</button>
                <button className="sd-na next" title="Next record" disabled={!nextRow || save.isPending} onClick={() => nextRow && void guardedExit(() => (viewOnly ? openView : openEdit)(nextRow.id))}>›</button>
              </div>
            </div>

            {actions()}

            <div className="sd-body div-detail">
              <div className="acc-tabs">
                <button type="button" className={`acc-tab${tab === 'basic' ? ' active' : ''}`} onClick={() => setTab('basic')}><i className="ti ti-info-circle" /> Basic Information</button>
                <button type="button" className={`acc-tab${tab === 'contact' ? ' active' : ''}`} onClick={() => setTab('contact')}><i className="ti ti-address-book" /> Contact</button>
                <button type="button" className={`acc-tab${tab === 'logo' ? ' active' : ''}`} onClick={() => setTab('logo')}><i className="ti ti-photo" /> Logo</button>
                <button type="button" className={`acc-tab${tab === 'qrcode' ? ' active' : ''}`} onClick={() => setTab('qrcode')}><i className="ti ti-qrcode" /> QR Code</button>
                <button type="button" className={`acc-tab${tab === 'mtf' ? ' active' : ''}`} onClick={() => setTab('mtf')}><i className="ti ti-checkbox" /> Mandatory Testing Flag</button>
                <button type="button" className={`acc-tab${tab === 'smtp' ? ' active' : ''}`} onClick={() => setTab('smtp')}><i className="ti ti-mail-cog" /> SMTP Configuration</button>
              </div>

              {/* Every field of the six tabs locks together in view mode; the tab buttons stay live. */}
              <fieldset className="sm-viewlock" disabled={viewOnly}>
              {tab === 'basic' && (
                <div className="acc-tab-pane">
                  {/* 3-column layout matching the legacy Company_DetailView Basic Information group. */}
                  <div className="acc-emp-2col acc-div-basic">
                    <div className="acc-emp-col">
                      {text('Division', 'companyName', true)}
                      {text('Web Site', 'webSite')}
                      {text('Office Phone', 'officePhone', true)}
                      {text('Address', 'address', true)}
                      <div className="acc-field-h">
                        <label className="acc-label">Language</label>
                        <div className="acc-fh-control" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                          {LANGUAGES.map((l) => (
                            <label key={l.v} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                              <input type="radio" name="div-lang" checked={(form.language ?? 1) === l.v} onChange={() => set({ language: l.v })} /> {l.t}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="acc-emp-col">
                      {lookup('City', 'city', cityOpts, (v) => set({ city: v }), true)}
                      {lookup('State', 'state', stateOpts, onState, true)}
                      {text('Zip Code', 'zip')}
                      {lookup('Country', 'country', countryOpts, onCountry, true)}
                      {/* LIMS renders this boolean as [checkbox] Is Valid (checkbox first), at the control column. */}
                      <div className="acc-field-h">
                        <label className="acc-label" />
                        <div className="acc-fh-control">
                          <label className="acc-check-inline"><input type="checkbox" checked={form.isValid} onChange={(e) => set({ isValid: e.target.checked })} /> Is Valid</label>
                        </div>
                      </div>
                    </div>
                    <div className="acc-emp-col">
                      <div className="acc-field-h">
                        <label className="acc-label acc-label-req">Time Zone<span className="req">*</span></label>
                        <div className="acc-fh-control acc-lookup">
                          <LookupSelect value={form.timeZone ?? null} options={tzOpts} onChange={(v) => { set({ timeZone: v }); if (v) { clearFieldErr('timeZone'); clearFieldErr('utc'); } }} />
                          {form.timeZone && <button className="acc-lookup-btn" title="Erase" onClick={() => set({ timeZone: null })}><i className="ti ti-eraser" /></button>}
                        </div>
                      </div>
                      {fieldErr('timeZone')}
                      <div className="acc-field-h">
                        <label className="acc-label acc-label-req">UTC<span className="req">*</span></label>
                        <div className="acc-fh-control"><input className="acc-input" value={dlgUtc} disabled readOnly /></div>
                      </div>
                      {fieldErr('utc')}
                    </div>
                  </div>
                  <div className="acc-field-h acc-div-remark">
                    <label className="acc-label">Remark</label>
                    <div className="acc-fh-control"><textarea className="acc-input acc-text-multi" rows={2} value={form.remark ?? ''} onChange={(e) => set({ remark: e.target.value })} /></div>
                  </div>
                </div>
              )}

              {tab === 'contact' && (
                // Two equal columns, both starting at the top: Contact Name heads the left one (owner request 2026-10-09).
                <div className="acc-tab-pane acc-contact">
                  <div className="acc-contact-cols">
                    <div className="acc-emp-col">
                      {text('Contact Name', 'contactName')}
                      {text('Office Phone', 'officePhone', true)}
                      {text('Mobile Phone', 'mobilePhone')}
                      {text('Fax', 'fax')}
                    </div>
                    <div className="acc-emp-col">
                      {text('Home Phone', 'homePhone')}
                      {text('Other Phone', 'otherPhone')}
                      {text('Email', 'email')}
                    </div>
                  </div>
                </div>
              )}

              {tab === 'logo' && imageField('Logo', 'logo')}
              {tab === 'qrcode' && imageField('QR Code', 'qrCode')}
              {tab === 'mtf' && imageField('Measure CV Flag', 'measureCvFlag')}

              {tab === 'smtp' && (
                // SMTP Configuration (LIMS): fields disable when Enable SMTP is off (Appearance "Disable SMTP").
                <div className="acc-tab-pane acc-compact">
                  <div className="dtm-layout">
                    <div className="dtm-fields">
                      <div className="acc-field-h">
                        <label className="acc-label" />
                        <div className="acc-fh-control"><label className="acc-check-inline"><input type="checkbox" checked={form.enableSmtp} onChange={(e) => set({ enableSmtp: e.target.checked })} /> Enable SMTP</label></div>
                      </div>
                      <div className="acc-field-h">
                        <label className="acc-label">Mail Server</label>
                        <div className="acc-fh-control"><input className="acc-input" value={form.mailServer ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ mailServer: e.target.value })} /></div>
                      </div>
                      <div className="acc-field-h">
                        <label className="acc-label">Port</label>
                        <div className="acc-fh-control"><input className="acc-input" type="number" value={form.portNo ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ portNo: e.target.value === '' ? null : Number(e.target.value) })} /></div>
                      </div>
                      <div className="acc-field-h">
                        <label className="acc-label" />
                        <div className="acc-fh-control"><label className="acc-check-inline"><input type="checkbox" checked={form.ssl} disabled={!form.enableSmtp} onChange={(e) => set({ ssl: e.target.checked })} /> SSL</label></div>
                      </div>
                      <div className="acc-field-h">
                        <label className="acc-label">From Mail</label>
                        <div className="acc-fh-control"><input className="acc-input" value={form.fromMail ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ fromMail: e.target.value })} /></div>
                      </div>
                      <div className="acc-field-h">
                        <label className="acc-label">From Mail Password</label>
                        <div className="acc-fh-control"><input className="acc-input" type="password" autoComplete="new-password" value={form.fromMailPassword ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ fromMailPassword: e.target.value })} /></div>
                      </div>
                      <div className="acc-field-h">
                        <label className="acc-label">Display Name</label>
                        <div className="acc-fh-control"><input className="acc-input" value={form.displayName ?? ''} disabled={!form.enableSmtp} onChange={(e) => set({ displayName: e.target.value })} /></div>
                      </div>
                      {/* Legacy places Test Connection under Display Name; it is live only while Enable SMTP is ticked.
                          No popup: it sends the fixed test mail and reports the result in the banner, as legacy. */}
                      <div className="div-smtp-test">
                        <button type="button" className="ca-btn" disabled={!form.enableSmtp || testSmtp.isPending} onClick={() => void testConnection()}>{testSmtp.isPending ? 'Testing...' : 'Test Connection'}</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              </fieldset>

              {err && <div className="acc-err" style={{ marginTop: 8 }}>{err}</div>}

              {/* Bottom nested-grid tabs (Departments / Positions / Certificate), like the LIMS detail. */}
              <div className="acc-tabs" style={{ marginTop: 16 }}>
                <button type="button" className={`acc-tab${btab === 'departments' ? ' active' : ''}`} onClick={() => setBtab('departments')}><i className="ti ti-sitemap" /> Departments</button>
                <button type="button" className={`acc-tab${btab === 'positions' ? ' active' : ''}`} onClick={() => setBtab('positions')}><i className="ti ti-id-badge-2" /> Positions</button>
                <button type="button" className={`acc-tab${btab === 'certificate' ? ' active' : ''}`} onClick={() => setBtab('certificate')}><i className="ti ti-certificate" /> Certificate</button>
              </div>
              <div className={viewOnly ? 'sm-viewlock-panel' : undefined}>
                {btab === 'departments' && <DivisionDepartmentsPanel companyId={editId ?? undefined}
                  staged={staging.departmentIds} onStaged={(ids) => setStaging((st) => ({ ...st, departmentIds: ids }))} />}
                {btab === 'positions' && <DivisionPositionsPanel companyId={editId ?? undefined}
                  staged={staging.positionIds} onStaged={(ids) => setStaging((st) => ({ ...st, positionIds: ids }))} />}
                {btab === 'certificate' && <DivisionCertificatePanel companyId={editId ?? undefined}
                  stagedLinks={staging.certificateLinkIds} stagedDrafts={staging.certificateDrafts}
                  onStaged={(links, drafts) => setStaging((st) => ({ ...st, certificateLinkIds: links, certificateDrafts: drafts }))} />}
              </div>
            </div>

            {actions(true)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Division</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              if (id) openView(id);
            }} />
          <FormTitle pageId="sm-division" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Division" />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        {!gridMax && (
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="Division"
                headers={['Name', 'OfficePhone', 'Address', 'City', 'State', 'Zip', 'Country', 'Department', 'TimeZone', 'UTC']}
                rows={visible.map((r) => [r.name ?? '', r.officePhone ?? '', r.address ?? '', r.city ?? '', r.state ?? '', r.zip ?? '', r.country ?? '', r.department ?? '', r.timeZone ?? '', r.utc ?? ''])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="Division" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<Company> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }} selectedRowId={editId ?? null}
            onRowClick={(r) => openView(r.id)}
            initialSort={{ key: 'name', dir: 'asc' }} loading={isFetching} emptyText="No divisions found." hideHeaderTools />
        </div>
      </div>

    </div>
  );
}
